import { Skeleton } from "@/components/ui/skeleton";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { listingCardTransitionName } from "@/lib/native/listingNavigation";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";

type NativeCarDetailsSkeletonProps = {
  listingId: string;
  className?: string;
};

/** Native listing details loading skeleton (keeps view-transition hero). */
export function NativeCarDetailsSkeleton({
  listingId,
  className,
}: NativeCarDetailsSkeletonProps) {
  const { language } = useLanguage();
  const labels = carDetailsLabels(language);

  return (
    <div
      className={cn(
        "min-h-screen native-listing-page bg-transparent relative",
        className,
      )}
    >
      <div className="container max-w-6xl py-0 px-0 relative z-10">
        <div className="px-[var(--native-page-inset-x)] pt-0 pb-0 -mb-1">
          <BtnNavigateBack
            label={labels.back}
            preferHistory
            to="/auctions"
          />
        </div>
        {/* Hero image placeholder */}
        <div
          className="native-listing-gallery-bleed rounded-none overflow-hidden border-y border-border/60 bg-card"
          style={
            ({
              viewTransitionName: listingCardTransitionName(listingId),
            } as React.CSSProperties)
          }
        >
          <Skeleton className="w-full aspect-[4/3]" />
          <div className="p-3 space-y-2">
            <Skeleton className="h-5 w-[70%]" />
            <Skeleton className="h-4 w-[40%]" />
            <div className="flex gap-2 pt-1">
              <Skeleton className="h-7 w-24 rounded-full" />
              <Skeleton className="h-7 w-20 rounded-full" />
            </div>
          </div>
        </div>

        {/* Body placeholders (basic info + bidding) */}
        <div className="mt-4 px-[var(--native-page-inset-x)] space-y-3">
          <div className="rounded-2xl border border-border/60 bg-card p-3 space-y-2">
            <Skeleton className="h-4 w-[55%]" />
            <div className="grid grid-cols-2 gap-2">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-[85%]" />
              <Skeleton className="h-3 w-[75%]" />
            </div>
          </div>

          <div className="rounded-2xl border border-border/60 bg-card p-3 space-y-2">
            <Skeleton className="h-4 w-[45%]" />
            <Skeleton className="h-10 w-full" />
            <div className="grid grid-cols-2 gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>

          <div className="rounded-2xl border border-border/60 bg-card p-3 space-y-2">
            <Skeleton className="h-4 w-[35%]" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

